import { Helmet } from "react-helmet";
import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../components/Header";
import { useHookPromiseToPay } from "../../../../hooks/useHookPromiseToPay";
import { useNavigate } from "react-router-dom";
import {
  FaChevronRight,
  FaChevronLeft,
  FaAnglesLeft,
  FaAnglesRight,
  FaHandshake,
  FaHashtag,
  FaMagnifyingGlass,
  FaFilter,
  FaUser,
  FaPhone,
  FaCalendarCheck,
  FaPlus,
  FaPencil,
  FaEye,
  FaTrash,
  FaUserTie,
  FaWhatsapp,
  // Icons tambahan untuk Filter Drawer
  FaXmark,
  FaRotateLeft,
  FaArrowDownShortWide,
  FaArrowUpShortWide,
} from "react-icons/fa6";
import { SelectNas } from "../../../../components/forms/selects/SelectNas";
import { SelectPop } from "../../../../components/forms/selects/SelectPop";
import { SelectOdc } from "../../../../components/forms/selects/SelectOdc";
import { SelectOdp } from "../../../../components/forms/selects/SelectOdp";
import { BtnDefault } from "../../../../components/buttons/BtnDefault";
import { CreateNewPromiseToPayModal } from "./modals/create";
import { UpdatePromiseToPayModal } from "./modals/update";
import { DeletePromiseToPayModal } from "./modals/delete";

/* ============================================
   KOMPONEN: Skeleton
   ============================================ */
const SkeletonRow = ({ cols = 7 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3.5 bg-slate-700/60 rounded-md"
          style={{ width: `${40 + Math.random() * 50}%` }}
        />
      </td>
    ))}
  </tr>
);

const SkeletonCard = () => (
  <div
    className="rounded-2xl overflow-hidden animate-pulse"
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
    }}
  >
    <div
      className="px-4 py-3 flex justify-between items-center"
      style={{ background: "rgba(15, 23, 42, 0.7)", borderBottom: "1px solid rgba(51, 65, 85, 0.2)" }}
    >
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-slate-700/60" />
        <div className="h-3 bg-slate-700/50 rounded-md w-20" />
      </div>
      <div className="h-6 bg-slate-700/40 rounded-full w-16" />
    </div>
    <div className="p-4">
      <div className="space-y-2 mb-3">
        <div className="h-5 bg-slate-700/60 rounded-md w-36" />
        <div className="h-3 bg-slate-700/30 rounded-md w-28" />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="h-8 bg-slate-700/30 rounded-lg" />
        <div className="h-8 bg-slate-700/30 rounded-lg" />
      </div>
      <div
        className="flex justify-between items-center pt-3"
        style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}
      >
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-slate-700/50" />
          <div className="h-3 bg-slate-700/30 rounded w-10" />
        </div>
        <div className="h-3 bg-slate-700/30 rounded w-16" />
      </div>
    </div>
  </div>
);

/* ============================================
   KOMPONEN: Helper
   ============================================ */
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const months = [
    "Jan", "Feb", "Mar", "Apr", "Mei", "Jun",
    "Jul", "Agu", "Sep", "Okt", "Nov", "Des",
  ];
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const isOverdue = (dateStr, status) => {
  if (status === "lunas" || status === "batal" || !dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(dateStr);
  return targetDate < today;
};

const JanjiBayarCell = ({ dateStr, status }) => {
  const overdue = isOverdue(dateStr, status);
  return (
    <div className="flex items-center gap-2">
      <FaCalendarCheck
        className={`text-xs flex-shrink-0 ${overdue ? "text-red-400" : "text-slate-500"}`}
      />
      <span
        className={`text-sm font-medium ${overdue ? "text-red-400" : "text-gray-100"}`}
      >
        {formatDate(dateStr)}
      </span>
    </div>
  );
};

/* ============================================
   CONFIG & DRAWER COMPONENT
   ============================================ */
const FILTER_DEFAULTS = {
  search: "",
  nas_id: "",
  pop_id: "",
  odc_id: "",
  odp_id: "",
  status: "",
  sort_field: "date",
  sort: "desc",
  per_page: 10,
};

const STATUS_OPTIONS = [
  { value: "", label: "Semua" },
  { value: "terlewat", label: "Terlewat" },
  { value: "mendatang", label: "Mendatang" },
];

const SORT_OPTIONS = [
  { value: "date", label: "Tanggal Janji Bayar" },
  { value: "created_at", label: "Tanggal Dibuat" },
  { value: "status", label: "Status" },
];

/* --- Filter Drawer Component --- */
const FilterDrawer = ({ open, setOpen, currentParams, onApply }) => {
  const panelRef = useRef(null);
  const [form, setForm] = useState(FILTER_DEFAULTS);

  // Sync form saat drawer dibuka
  useEffect(() => {
    if (open) {
      setForm({
        search: currentParams.search || "",
        nas_id: currentParams.nas_id || "",
        pop_id: currentParams.pop_id || "",
        odc_id: currentParams.odc_id || "",
        odp_id: currentParams.odp_id || "",
        status: currentParams.status || "",
        sort_field: currentParams.sort_field || "date",
        sort: currentParams.sort || "desc",
        per_page: currentParams.per_page || 10,
      });
    }
  }, [open, currentParams]);

  // Lock body scroll
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleReset = () => {
    setForm(FILTER_DEFAULTS);
  };

  const handleApply = () => {
    onApply(form);
    setOpen(false);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setOpen(false);
  };

  const activeCount = Object.entries(form).filter(([key, val]) => {
    if (key === "sort_field" || key === "sort") return false;
    return val !== "" && val !== null && val !== undefined;
  }).length;

  return (
    <>
      <div
        className={`fixed inset-0 z-[998] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={handleBackdropClick}
      />
      <div
        ref={panelRef}
        className={`fixed top-0 right-0 z-[999] h-full w-full max-w-md shadow-2xl transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`}
        style={{
          background: "rgba(15, 23, 42, 0.98)",
          borderLeft: "1px solid rgba(51, 65, 85, 0.3)",
          backdropFilter: "blur(20px)",
        }}
      >
        {/* Header Drawer */}
        <div
          className="flex items-center justify-between px-6 py-5"
          style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.25)" }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background: "rgba(0, 180, 216, 0.12)",
                border: "1px solid rgba(0, 180, 216, 0.2)",
              }}
            >
              <FaFilter className="text-sm text-cyan-400" />
            </div>
            <div>
              <h3 className="font-bold text-gray-100 text-sm">Filter Data</h3>
              {activeCount > 0 && (
                <p className="text-[10px] text-cyan-400 mt-0.5">
                  {activeCount} filter aktif
                </p>
              )}
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-all"
          >
            <FaXmark size={18} />
          </button>
        </div>

        {/* Body Drawer */}
        <div className="overflow-y-auto px-6 py-5 space-y-5" style={{ height: "calc(100% - 140px)" }}>
          
          {/* Infrastruktur */}
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Wilayah</p>
            <div className="space-y-3">
              <SelectNas
                placeholder="Semua NAS"
                value={form.nas_id}
                searchable
                setValue={(id) => setForm(f => ({ ...f, nas_id: id, pop_id: "", odc_id: "", odp_id: "" }))}
              />
              <SelectPop
                placeholder="Semua POP"
                nas_id={form.nas_id}
                value={form.pop_id}
                searchable
                disabled={!form.nas_id}
                setValue={(id) => setForm(f => ({ ...f, pop_id: id, odc_id: "", odp_id: "" }))}
              />
              <SelectOdc
                placeholder="Semua ODC"
                pop_id={form.pop_id}
                value={form.odc_id}
                searchable
                disabled={!form.pop_id}
                setValue={(id) => setForm(f => ({ ...f, odc_id: id, odp_id: "" }))}
              />
              <SelectOdp
                placeholder="Semua ODP"
                pop_id={form.odc_id}
                value={form.odp_id}
                searchable
                disabled={!form.odc_id}
                setValue={(id) => setForm(f => ({ ...f, odp_id: id }))}
              />
            </div>
          </div>

          <div style={{ borderTop: "1px dashed rgba(51, 65, 85, 0.25)" }} />

          {/* Status & Sort */}
          <div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Klasifikasi</p>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
                >
                  <option value="">Semua Status</option>
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>

              <div>
                 <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">Pengurutan</label>
                 <div className="grid grid-cols-2 gap-3">
                    <select
                        value={form.sort_field}
                        onChange={(e) => setForm(f => ({ ...f, sort_field: e.target.value }))}
                        className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 transition-all appearance-none"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
                    >
                        {SORT_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <button
                        type="button"
                        onClick={() => setForm(f => ({ ...f, sort: f.sort === "desc" ? "asc" : "desc" }))}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm rounded-xl border transition-all"
                        style={{
                            background: form.sort === "desc" ? "rgba(0, 180, 216, 0.08)" : "rgba(251, 146, 60, 0.08)",
                            borderColor: form.sort === "desc" ? "rgba(0, 180, 216, 0.2)" : "rgba(251, 146, 60, 0.2)",
                            color: form.sort === "desc" ? "#22d3ee" : "#fb923c",
                        }}
                    >
                        {form.sort === "desc" ? <><FaArrowDownShortWide size={14} /> Desc</> : <><FaArrowUpShortWide size={14} /> Asc</>}
                    </button>
                 </div>
              </div>
             </div>
          </div>
        </div>

        {/* Footer Drawer */}
        <div
          className="absolute bottom-0 left-0 right-0 flex gap-3 px-6 py-4"
          style={{
            background: "rgba(15, 23, 42, 0.98)",
            borderTop: "1px solid rgba(51, 65, 85, 0.25)",
            backdropFilter: "blur(20px)",
          }}
        >
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center justify-center gap-2 flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all"
            style={{
              background: "rgba(51, 65, 85, 0.15)",
              borderColor: "rgba(51, 65, 85, 0.3)",
              color: "#94a3b8",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)";
              e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.2)";
              e.currentTarget.style.color = "#f87171";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(51, 65, 85, 0.15)";
              e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)";
              e.currentTarget.style.color = "#94a3b8";
            }}
          >
            <FaRotateLeft size={13} /> Reset
          </button>
          <BtnDefault type="button" onClick={handleApply} className="flex-1 justify-center py-2.5">
            Terapkan
          </BtnDefault>
        </div>
      </div>
    </>
  );
};

const FilterChip = ({ label, onRemove }) => (
  <span
    className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg text-[11px] font-semibold cursor-default"
    style={{
      background: "rgba(0, 180, 216, 0.08)",
      border: "1px solid rgba(0, 180, 216, 0.2)",
      color: "#22d3ee",
    }}
  >
    {label}
    <button onClick={onRemove} className="p-0.5 rounded-md hover:bg-cyan-400/20 transition-colors">
      <FaXmark size={10} />
    </button>
  </span>
);

/* ============================================
   KOMPONEN UTAMA
   ============================================ */
export const PromiseToPay = () => {
  const [showSidebar, setShowSidebar] = useState(true);
  const navigate = useNavigate();
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openFilter, setOpenFilter] = useState(false);

  const [selectedData, setSelectedData] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const {
    data,
    meta,
    loading,
    params,
    setParams,
    refreshData,
  } = useHookPromiseToPay();

  // Hitung jumlah filter aktif (selain search, page, per_page)
  const activeFilterCount = ["nas_id", "pop_id", "odc_id", "odp_id", "status", "sort_field", "sort"]
    .filter((key) => params[key] && params[key] !== "" && params[key] !== FILTER_DEFAULTS[key]).length;

  /* ---------- Selection ---------- */
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === data?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(data?.map((item) => item.id));
    }
  };

  /* ---------- Pagination ---------- */
  const getPages = () => {
    if (!meta) return [];
    const total = meta.last_page;
    const current = meta.current_page;
    const delta = 2;
    const pages = [];
    const left = Math.max(2, current - delta);
    const right = Math.min(total - 1, current + delta);
    pages.push(1);
    if (left > 2) pages.push("...");
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  };

  /* ---------- Handlers ---------- */
  const handleFilterApply = (filterForm) => {
    setParams((prev) => ({
      ...prev,
      ...filterForm,
      page: 1,
    }));
  };

  const handleFilterReset = () => {
    setParams((prev) => ({
      ...prev,
      ...FILTER_DEFAULTS,
      page: 1,
    }));
  };

  const columns = [
    { key: "no", label: "No", align: "center", width: "60px" },
    { key: "action", label: "Aksi", align: "center" },
    { key: "internet_number", label: "No Internet", align: "left" },
    { key: "name", label: "Nama Pelanggan", align: "left" },
    { key: "phone", label: "No Telepon", align: "left" },
    { key: "janji_bayar", label: "Janji Bayar", align: "left" },
    { key: "description", label: "Keterangan", align: "left" },
    { key: "nas", label: "Nas", align: "left" },
    { key: "pop", label: "Pop", align: "left" },
    { key: "odc", label: "Odc", align: "left" },
    { key: "odp", label: "Odp", align: "left" },
  ];

  return (
    <>
      <Helmet>
        <title>Janji Bayar - DANELA NET</title>
        <meta name="description" content="Kelola data janji bayar pelanggan." />
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-3">
            <div
              className="p-2 rounded-xl"
              style={{
                background: "rgba(0, 180, 216, 0.1)",
                border: "1px solid rgba(0, 180, 216, 0.15)",
              }}
            >
              <FaHandshake className="text-sm text-cyan-400" />
            </div>
            <div>
              <h2 className="text-base md:text-xl font-bold text-gray-100">Janji Bayar</h2>
              <p className="text-xs text-slate-500 hidden sm:block">Data pelanggan yang memiliki janji pembayaran</p>
            </div>
            {meta && (
              <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                {meta.total} data
              </span>
            )}
          </div>
          <BtnDefault onClick={() => setOpenModalCreate(true)}>
            <FaPlus className="text-xs" />
            <span className="hidden sm:block">Tambah Data</span>
          </BtnDefault>
        </Header>

        <main className="p-4 lg:p-6 space-y-5">
          
          {/* --- STICKY SEARCH BAR --- */}
          <div className="flex items-center gap-3 sticky top-20 z-30 py-2 -mx-4 px-4 lg:mx-0 lg:px-0 transition-all duration-300 bg-transparent" 
               style={{ backdropFilter: "blur(8px)", borderBottom: "1px solid rgba(51,65,85,0.3)" }}>
            
            {/* Search Input */}
            <div className="relative flex-1">
              <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
              <input
                type="text"
                placeholder="Cari nama, ID pelanggan, no telepon..."
                value={params?.search}
                onChange={(e) => setParams((p) => ({ ...p, search: e.target.value, page: 1 }))}
                className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setOpenFilter(true)}
              className="relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all"
              style={{
                background: activeFilterCount > 0
                  ? "rgba(0, 180, 216, 0.12)"
                  : "rgba(51, 65, 85, 0.2)",
                border: activeFilterCount > 0
                  ? "1px solid rgba(0, 180, 216, 0.3)"
                  : "1px solid rgba(51, 65, 85, 0.3)",
                color: activeFilterCount > 0 ? "#22d3ee" : "#94a3b8",
              }}
            >
              <FaFilter className="text-xs" />
              <span className="hidden sm:inline">Filter</span>
              {activeFilterCount > 0 && (
                <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black bg-cyan-400 text-navy-900">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Quick Reset */}
            {activeFilterCount > 0 && (
              <button
                onClick={handleFilterReset}
                className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold text-red-400 transition-all bg-red-500/5 border border-red-500/10 hover:bg-red-500/10"
                title="Hapus semua filter"
              >
                <FaRotateLeft size={11} />
                <span className="hidden sm:inline">Reset</span>
              </button>
            )}
          </div>

          {/* --- ACTIVE FILTER CHIPS --- */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-300">
              {params.nas_id && <FilterChip label="NAS" onRemove={() => setParams((p) => ({ ...p, nas_id: "", pop_id: "", odc_id: "", odp_id: "", page: 1 }))} />}
              {params.pop_id && <FilterChip label="POP" onRemove={() => setParams((p) => ({ ...p, pop_id: "", odc_id: "", odp_id: "", page: 1 }))} />}
              {params.odc_id && <FilterChip label="ODC" onRemove={() => setParams((p) => ({ ...p, odc_id: "", odp_id: "", page: 1 }))} />}
              {params.odp_id && <FilterChip label="ODP" onRemove={() => setParams((p) => ({ ...p, odp_id: "", page: 1 }))} />}
              {params.status && <FilterChip label={`Status: ${params.status}`} onRemove={() => setParams((p) => ({ ...p, status: "", page: 1 }))} />}
              {(params.sort_field !== "date" || params.sort !== "desc") && (
                 <FilterChip label="Urutan Diubah" onRemove={() => setParams((p) => ({ ...p, sort_field: "date", sort: "desc", page: 1 }))} />
              )}
            </div>
          )}

          {/* ===== Desktop Table ===== */}
          <section
            className="hidden lg:block rounded-2xl overflow-hidden"
            style={{
              background: "rgba(15, 23, 42, 0.5)",
              border: "1px solid rgba(51, 65, 85, 0.3)",
            }}
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b" style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}>
                    <th className="px-4 py-3.5 text-center w-12">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === data?.length && data?.length > 0}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 focus:ring-offset-0 cursor-pointer"
                      />
                    </th>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"}`}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}>
                  {loading && !data && (
                    <>{[...Array(5)].map((_, i) => <SkeletonRow key={i} cols={columns.length + 1} />)}</>
                  )}
                  {!loading && data?.length === 0 && (
                    <tr>
                      <td colSpan={columns.length + 1} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}>
                            <FaHandshake className="text-2xl text-slate-600" />
                          </div>
                          <div>
                            <p className="text-slate-400 font-medium">Tidak ada janji bayar ditemukan</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {params?.search || activeFilterCount > 0 ? "Coba ubah filter atau kata kunci pencarian" : "Belum ada data janji bayar yang tersedia"}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!loading && data?.map((item, index) => (
                    <tr
                      key={item.id}
                      className="transition-colors duration-150"
                      style={{
                        background: isOverdue(item.date, item.status)
                          ? "rgba(239, 68, 68, 0.03)"
                          : "transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isOverdue(item.date, item.status)
                          ? "rgba(239, 68, 68, 0.06)"
                          : "rgba(0, 180, 216, 0.04)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isOverdue(item.date, item.status)
                          ? "rgba(239, 68, 68, 0.03)"
                          : "transparent";
                      }}
                    >
                      <td className="px-4 py-3 text-center">
                        <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelect(item.id)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500/30 cursor-pointer" />
                      </td>
                      <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">{meta.from + index}</td>
                      <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs flex items-center justify-center gap-2">
                        <button onClick={() => { setSelectedData(item); setOpenView(true); }} className="text-green-400 hover:text-green-500" title="WhatsApp"><FaWhatsapp /></button>
                        <button onClick={() => { setSelectedData(item); setOpenView(true); }} className="text-cyan-400 hover:text-cyan-500" title="Lihat"><FaEye /></button>
                        <button onClick={() => { setSelectedData(item); setOpenUpdate(true); }} className="text-amber-400 hover:text-amber-500" title="Ubah"><FaPencil /></button>
                        <button onClick={() => { setSelectedData(item); setOpenDelete(true); }} className="text-red-400 hover:text-red-500" title="Hapus"><FaTrash /></button>
                      </td>
                      <td className="px-4 py-3 min-w-[140px]">
                        <span className="font-mono text-xs text-cyan-400/80 bg-cyan-500/8 px-2 py-0.5 rounded">{item?.internet_number || "-"}</span>
                      </td>
                      <td className="px-4 py-3 min-w-[200px]">
                        <div className="flex items-center gap-2">
                          <FaUser className="text-xs text-slate-500" />
                          <span className="font-semibold text-gray-100">{item?.name || "-"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <FaPhone className="text-xs text-slate-500" />
                          <span className="text-sm text-slate-300">{item?.phone || "-"}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 min-w-[180px]">
                        <JanjiBayarCell dateStr={item?.date} status={item?.status} />
                      </td>
                      <td className="px-4 py-3 min-w-[130px]">
                        <span className="text-sm text-slate-300 italic line-clamp-1">{item?.description || "-"}</span>
                      </td>
                      <td className="px-4 py-3 min-w-[130px] text-slate-300">{item?.nas?.name || "-"}</td>
                      <td className="px-4 py-3 min-w-[130px] text-slate-300">{item?.pop?.name || "-"}</td>
                      <td className="px-4 py-3 min-w-[130px] text-slate-300">{item?.odc?.name || "-"}</td>
                      <td className="px-4 py-3 min-w-[130px] text-slate-300">{item?.odp?.name || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Desktop */}
            {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between px-5 py-3.5" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}>
                <p className="text-xs text-slate-500">
                  Menampilkan <span className="text-slate-300 font-medium">{meta.from}</span> sampai <span className="text-slate-300 font-medium">{meta.to}</span> dari <span className="text-slate-300 font-medium">{meta.total}</span>
                </p>
                <nav className="flex items-center gap-1">
                  <button onClick={() => setParams((p) => ({ ...p, page: 1 }))} disabled={meta.current_page === 1} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30"><FaAnglesLeft className="text-xs" /></button>
                  <button onClick={() => setParams((p) => ({ ...p, page: meta.current_page - 1 }))} disabled={meta.current_page === 1} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30"><FaChevronLeft className="text-xs" /></button>
                  {getPages().map((p, i) => p === "..." ? <span key={`d-${i}`} className="px-2 text-slate-600 text-sm">···</span> : (
                    <button key={p} onClick={() => setParams((p) => ({ ...p, page: p }))} className={`min-w-[34px] h-9 px-2 rounded-lg text-sm font-medium ${meta.current_page === p ? "bg-cyan-600 text-white" : "text-slate-400 hover:text-gray-100 hover:bg-slate-700/40"}`}>{p}</button>
                  ))}
                  <button onClick={() => setParams((p) => ({ ...p, page: meta.current_page + 1 }))} disabled={meta.current_page === meta.last_page} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30"><FaChevronRight className="text-xs" /></button>
                  <button onClick={() => setParams((p) => ({ ...p, page: meta.last_page }))} disabled={meta.current_page === meta.last_page} className="p-2 rounded-lg text-slate-400 hover:text-cyan-400 hover:bg-slate-700/40 disabled:opacity-30"><FaAnglesRight className="text-xs" /></button>
                </nav>
              </div>
            )}
          </section>

          {/* ===== Mobile Card List ===== */}
          <section className="lg:hidden space-y-3">
            {loading && !data && (<>{[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}</>)}
            {!loading && data?.length === 0 && (
              <div className="rounded-2xl p-12 text-center" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(51, 65, 85, 0.2)", border: "1px solid rgba(51, 65, 85, 0.2)" }}>
                  <FaHandshake className="text-2xl text-slate-600" />
                </div>
                <p className="text-slate-400 font-medium">Tidak ada janji bayar ditemukan</p>
              </div>
            )}
            {!loading && data?.map((item) => {
              const overdue = isOverdue(item.date, item.status);
              return (
                <article
                  key={item.id}
                  className="rounded-2xl overflow-hidden active:scale-[0.98] transition-all duration-200 cursor-pointer"
                  style={{
                    background: "rgba(15, 23, 42, 0.5)",
                    border: overdue ? "1px solid rgba(239, 68, 68, 0.15)" : "1px solid rgba(51, 65, 85, 0.3)",
                  }}
                  onClick={() => navigate(`/dashboard/promise-to-pay/detail/${item.id}`)}
                >
                  <div
                    className="px-4 py-3 flex justify-between items-center"
                    style={{
                      background: overdue ? "rgba(239, 68, 68, 0.04)" : "rgba(15, 23, 42, 0.7)",
                      borderBottom: `1px solid ${overdue ? "rgba(239, 68, 68, 0.1)" : "rgba(51, 65, 85, 0.2)"}`,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelect(item.id)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-cyan-500 cursor-pointer" />
                      <div className="p-1.5 rounded-lg" style={{ background: overdue ? "rgba(239, 68, 68, 0.1)" : "rgba(0, 180, 216, 0.1)", border: overdue ? "1px solid rgba(239, 68, 68, 0.15)" : "1px solid rgba(0, 180, 216, 0.15)" }}>
                        <FaHashtag className={`text-xs ${overdue ? "text-red-400" : "text-cyan-400"}`} />
                      </div>
                      <span className="text-xs font-bold text-slate-300 font-mono">{item?.internet_number || "-"}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={(e) => { e.stopPropagation(); setSelectedData(item); setOpenView(true); }} className="text-green-400"><FaEye /></button>
                        <button onClick={(e) => { e.stopPropagation(); setSelectedData(item); setOpenUpdate(true); }} className="text-amber-400"><FaPencil /></button>
                        <button onClick={(e) => { e.stopPropagation(); setSelectedData(item); setOpenDelete(true); }} className="text-red-400"><FaTrash /></button>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="space-y-1 mb-3">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-1.5 text-slate-400 mt-2"><FaUserTie className="text-[10px]" /><h3 className="font-semibold text-gray-100 text-sm">{item?.name || "Tidak ada nama"}</h3></div>
                          <div className="flex items-center gap-1.5 text-slate-400 mt-2"><FaPhone className="text-[10px]" /><span className="text-xs font-medium">{item?.phone || "-"}</span></div>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Janji Bayar</p>
                          <div className="flex items-center gap-1.5">
                            <FaCalendarCheck className={`text-xs ${overdue ? "text-red-400" : "text-cyan-400"}`} />
                            <span className={`text-xs font-semibold ${overdue ? "text-red-400" : "text-gray-100"}`}>{formatDate(item?.date)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="pt-5">
                        <h3>Keterangan</h3>
                        <p className="text-slate-500 italic line-clamp-1 text-xs">{item?.description || "-"}</p>
                      </div>
                    </div>
                    <div style={{ padding: "10px 12px", background: "rgba(15, 23, 42, 0.4)", borderRadius: "12px", border: "1px solid rgba(51, 65, 85, 0.15)" }}>
                      <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Wilayah</p>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                        <p className="text-xs text-slate-300 truncate">{item?.nas?.name || "-"}</p>
                        <p className="text-xs text-slate-300 truncate">{item?.pop?.name || "-"}</p>
                        <p className="text-xs text-slate-300 truncate">{item?.odp?.name || "-"}</p>
                        <p className="text-xs text-slate-300 truncate">{item?.odc?.name || "-"}</p>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
             {/* Mobile Pagination */}
             {meta && meta.last_page > 1 && (
              <div className="flex items-center justify-between pt-3 pb-6 px-1" style={{ borderTop: "1px solid rgba(51, 65, 85, 0.15)" }}>
                <button onClick={() => setParams((p) => ({ ...p, page: meta.current_page - 1 }))} disabled={meta.current_page === 1} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                  <FaChevronLeft className="text-xs" /> Sebelumnya
                </button>
                <span className="text-sm font-medium text-slate-400"><span className="text-cyan-400">{meta.current_page}</span><span className="text-slate-600 mx-1">/</span>{meta.last_page}</span>
                <button onClick={() => setParams((p) => ({ ...p, page: meta.current_page + 1 }))} disabled={meta.current_page === meta.last_page} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-300 disabled:opacity-30" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                  Berikutnya <FaChevronRight className="text-xs" />
                </button>
              </div>
            )}
          </section>
        </main>
      </DashboardLayout>

      {/* Modals */}
      <CreateNewPromiseToPayModal openModal={openModalCreate} setOpenModal={setOpenModalCreate} refresh={refreshData} />
      <UpdatePromiseToPayModal openModal={openUpdate} setOpenModal={setOpenUpdate} data={selectedData} refresh={refreshData} />
      <DeletePromiseToPayModal openModal={openDelete} setOpenModal={setOpenDelete} data={selectedData} refresh={refreshData} />
      
      {/* Filter Drawer */}
      <FilterDrawer 
        open={openFilter} 
        setOpen={setOpenFilter} 
        currentParams={params} 
        onApply={handleFilterApply} 
      />
    </>
  );
};